import axios from 'axios';

const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
const CACHE_PREFIX = 'gravatar:v2:';

function withQuery(url, params) {
    const u = new URL(url);
    Object.entries(params).forEach(([key, value]) => {
        u.searchParams.set(key, String(value));
    });
    return u.toString();
}

// Gravatar always returns an avatar, 404 when there's
// no custom photo so treat 404 as 'no photo'
async function hasRealAvatar(avatarUrl) {
    try {
        await axios.head(withQuery(avatarUrl, { d: 404 }));
        return true;
    } catch {
        return false;
    }
}

export async function fetchGravatarProfile(profileIdentifier) {
    if (!profileIdentifier) return undefined;

    const cacheKey = `${CACHE_PREFIX}${profileIdentifier}`;

    try {
        // serve from cache if it's less than an hour old
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_TTL_MS) {
                return data;
            }
        }

        // fetch the profile from Gravatar
        const { data: profile } = await axios.get(
            `https://api.gravatar.com/v3/profiles/${profileIdentifier}`
        );

        // keep the avatar only if it's a real (non-default) photo
        let avatarUrl = profile.avatar_url;
        if (!avatarUrl || !(await hasRealAvatar(avatarUrl))) {
            avatarUrl = undefined;
        } else {
            avatarUrl = withQuery(avatarUrl, { s: 240 });
        }

        // shape, cache, and return
        const result = {
            avatar_url: avatarUrl,
            profile_url: profile.profile_url,
        };
        localStorage.setItem(
            cacheKey,
            JSON.stringify({ data: result, timestamp: Date.now() })
        );
        return result;
    } catch (error) {
        console.error(
            `Gravatar fetch failed for "${profileIdentifier}":`,
            error
        );
        return undefined;
    }
}
